import React from 'react'
import type { Booking } from 'repositories/BerthApi/types/booking'
import type { BookingMasterData } from 'repositories/BerthApi/getBookingMasterData/type'

import type { SnackbarPayload } from 'components/Snackbar/useSnackbar'
import { BOOKING_STATUS } from 'constants/Booking'

import { useBodyScrollLock } from '../../hooks/useBodyScrollLock'
import { useModalScroll } from '../../hooks/useModalScroll'
import { getStatusLabel, useBookingStatusChange } from '../../hooks/useBookingStatusChange'
import type { BerthCard } from '../../types'
import { timeToMinutes } from '../../utils/timeConvert'
import { BookingDetailCard } from '../BookingDetailCard'
import { Dialog } from '../Dialog'

import styles from './index.module.css'

type Props = {
  berthName: string
  date: string
  cards: BerthCard[]
  bookingsMap: Record<string, Booking>
  masterData: BookingMasterData | null
  tenantId?: string | null
  selectedCardId: string | null
  onClose: () => void
  onNotify: (payload: SnackbarPayload) => void
  onEditClick?: (bookingKey: string) => void
}

const formatDateForTitle = (date: string): string => {
  const m = date.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return date
  return `${m[1]}/${m[2]}/${m[3]}`
}

const STATUS_CONFIRM_MESSAGES: Record<number, string> = {
  [BOOKING_STATUS.Confirmed]: '予約を確定しますか？',
  [BOOKING_STATUS.Calling]: '呼び出しますか？',
  [BOOKING_STATUS.Working]: '作業を開始しますか？',
  [BOOKING_STATUS.Finish]: '作業を完了しますか？',
}

export const CardDetailModal: React.FC<Props> = ({
  berthName,
  date,
  cards,
  bookingsMap,
  masterData,
  tenantId,
  selectedCardId,
  onClose,
  onNotify,
  onEditClick,
}) => {
  const isOpen = selectedCardId !== null
  const { focusedCardId, cardRefs, scrollAreaRef, handleCardClick } = useModalScroll(
    selectedCardId,
    isOpen
  )

  const {
    pendingAction,
    forceFinish,
    isDialogLoading,
    handleStatusChange,
    handleConfirmOk,
    handleConfirmCancel,
    handleForceFinishOk,
    handleForceFinishCancel,
  } = useBookingStatusChange({ tenantId, bookingsMap, onNotify })

  useBodyScrollLock(isOpen)

  if (!isOpen) return null

  // 未割当を除外し、開始時刻順 → 同一時刻は作成日時の昇順でソート
  const sortedCards = [...cards]
    .filter((c) => c.status !== BOOKING_STATUS.Unassigned)
    .sort((a, b) => {
      const timeDiff = timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
      if (timeDiff !== 0) return timeDiff
      const aCreatedDate = bookingsMap[a.id]?.createdDate ?? ''
      const bCreatedDate = bookingsMap[b.id]?.createdDate ?? ''
      return aCreatedDate.localeCompare(bCreatedDate)
    })

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose()
  }

  return (
    <>
      <div className={styles.overlay} onClick={handleOverlayClick}>
        <div className={styles.content}>
          <div className={styles.header}>
            <h3 className={styles.title}>
              {formatDateForTitle(date)} {berthName} 予約一覧
            </h3>
            <button className={styles.closeIconButton} onClick={onClose} aria-label="閉じる">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </div>

          <div className={styles.scrollArea} ref={scrollAreaRef}>
            {sortedCards.map((card) => {
              const booking = bookingsMap[card.id]
              if (!booking) return null

              return (
                <BookingDetailCard
                  key={card.id}
                  booking={booking}
                  bookingKey={card.id}
                  masterData={masterData}
                  highlighted={card.id === focusedCardId}
                  onCardClick={() => handleCardClick(card.id)}
                  onStatusChange={handleStatusChange}
                  onEditClick={onEditClick}
                  cardRef={(el) => {
                    cardRefs.current[card.id] = el
                  }}
                />
              )
            })}
          </div>
        </div>
      </div>

      {pendingAction && (
        <Dialog
          onClose={handleConfirmCancel}
          prevButtonProps={{
            label: 'キャンセル',
            onClick: handleConfirmCancel,
            variant: 'cancel',
            disabled: isDialogLoading,
          }}
          nextButtonProps={{
            label: 'OK',
            onClick: handleConfirmOk,
            variant: 'primary',
            disabled: isDialogLoading,
          }}
        >
          <p className={styles.dialogMessage}>
            {STATUS_CONFIRM_MESSAGES[pendingAction.nextStatus] || 'ステータスを変更しますか？'}
          </p>
        </Dialog>
      )}

      {forceFinish && (
        <Dialog
          onClose={handleForceFinishCancel}
          prevButtonProps={{
            label: 'キャンセル',
            onClick: handleForceFinishCancel,
            variant: 'cancel',
            disabled: isDialogLoading,
          }}
          nextButtonProps={{
            label: 'OK',
            onClick: handleForceFinishOk,
            variant: 'primary',
            disabled: isDialogLoading,
          }}
        >
          <p className={styles.dialogMessage}>
            以下の予約がまだ{getStatusLabel(forceFinish.previousBooking.status ?? null)}ですが、
            <br />
            完了にしてよいですか？
          </p>
          <BookingDetailCard
            booking={forceFinish.previousBooking}
            masterData={masterData}
            hideEditAction
            hideStatusAction
          />
        </Dialog>
      )}
    </>
  )
}
